import { useStytch, useStytchSession, useStytchUser } from '@stytch/nextjs';
import { DateTime } from 'luxon';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import theraToast from 'components/generic-components/toast/TheraToast';
import {
  hasSkippedPaymentMethodOnboarding,
  isReferralBannerDismissed,
  markReferralBannerDismissed,
  restoreOnboardingDraft,
  snapshotOnboardingDraft,
} from 'lib/localStorageConstants';
import { useTheraFernClient } from 'hooks/useFern';
import { SKIP_2FA_EMAILS } from 'lib/whitelistConstants';
import { SESSION_DURATION_MINUTES } from 'pages/authenticate';
import { AdminRoutes, AuthRoutes, isAlwaysPublicRoute } from 'routes';
import { clearSWRCache } from 'lib/swrCacheProvider';
import { cancelAxiosRequests } from 'services/config';
import { getUserAffiliations, getUserProfile } from 'services/userApi';
import { ICompanyAffiliation, IEmployment } from 'types';
import { get_ls_saved_values, LS_key_Last_Used_Auth_Method } from 'utils/affiliations';
import { logError } from 'utils/error';
import { captureError } from 'utils/datadog-error';
import { buildIntercomCompanies, updateIntercomSettings } from 'utils/intercom';
import {
  checkIfEmailVerified,
  checkIfNewInvitedUserWithoutPasswordAndNoOAuth,
  checkIfSuperAdminMode,
  checkIfSupportAdminMode,
  clearLocalStorage,
  clearLSEmailProps,
  getStytch404RedirectRoute,
  getTransformedAffiliations,
  getUserContextData,
  routeToRedirectToEmailVerification,
  routeToRedirectToSetPassword,
  isStytchProdEnv,
  setPostHogAndDatadogUser,
  THEME_STORAGE_KEY,
  THERA_Skip2FA,
  THERA_SkipEmailVerify,
  THERA_SuperAdmin,
  THERA_SupportAdmin,
} from 'utils/user';
import { initialState, userReducer } from './user.store';
import { _IUser, actionTypes, IUserContext, UserState } from './user.type';

const skip2FA = process.env.NEXT_PUBLIC_SKIP_2FA === 'true';

/**
 * Workflows:
 * The UserProvider is a wrapper component that provides the user context to all the children components.
 * It also fetches the user profile from the API and sets it in the context.
 * After fetching the user profile, it also checks if the user is a contractor or an employee and redirects them to the appropriate route.
 * If email is not verified, it redirects the user to the verify email page.
 * In case of a 404, it assumes that stytch user exists but user profile does not exist and redirects the user to the admin onboarding page.
 */

// Context
const UserContext = createContext<IUserContext>({} as IUserContext);
const UserProvider = ({ children }) => {
  const { theraFernApi } = useTheraFernClient();
  const { user: stytchUser } = useStytchUser();
  const { session } = useStytchSession();
  const router = useRouter();
  const stytch = useStytch();
  const hasSessionExpired = session?.expires_at ? session?.expires_at < DateTime.utc().toISO() : undefined;

  const stytch_token_type = router?.query?.stytch_token_type?.toString();
  const token = router?.query?.token?.toString();
  const isAuthenticating = router.pathname === AuthRoutes.Authenticate && token && stytch_token_type ? true : false;

  // Cached server data belongs to one Stytch user. logout() clears it, but a session that expires
  // in-tab followed by a different sign-in never passes through logout(), so clear on any handover.
  const lastStytchUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    const stytchUserId = stytchUser?.user_id ?? null;
    if (lastStytchUserIdRef.current && stytchUserId !== lastStytchUserIdRef.current) {
      clearSWRCache();
    }
    lastStytchUserIdRef.current = stytchUserId;
  }, [stytchUser?.user_id]);

  // Lazy Initialization (if needed)
  const lazyInitialState = (): UserState => {
    let storedTeamId: string | null = null;

    // Check if window and localStorage are available (i.e., code is running in the browser)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      storedTeamId = localStorage.getItem('currentTeamId');
    }

    return {
      ...initialState,
      currentTeamId: storedTeamId || null,
    };
  };
  const [state, dispatch] = useReducer(userReducer, initialState, lazyInitialState);

  /**
   * Keep a ref to the latest `isUserProfileReady` so `fetchUserProfile` can make
   * correct toast decisions even if the callback is memoized without depending
   * on `state.isUserProfileReady` (we intentionally keep the dependency list small).
   */
  const isUserProfileReadyRef = useRef<boolean>(state.isUserProfileReady);
  useEffect(() => {
    isUserProfileReadyRef.current = state.isUserProfileReady;
  }, [state.isUserProfileReady]);

  useEffect(() => {
    // Only interact with localStorage on the client side
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      if (state.currentTeamId) {
        localStorage.setItem('currentTeamId', state.currentTeamId);
      } else {
        localStorage.removeItem('currentTeamId');
      }
    }
  }, [state.currentTeamId]);

  const resetState = useCallback(() => {
    dispatch({ type: actionTypes.RESET_STATE });
  }, []);

  let currentUser: _IUser = {} as any;
  let userFullName: string;
  let userCreatedAtTimestamp: number;
  let routeToRedirect: string;

  const logout = useCallback(
    async (redirectToSignIn = true, revokeStytchSession = true, preserveOnboardingDraft = false) => {
      dispatch({
        type: actionTypes.SET_LOADING_AND_LOGGING_OUT,
        payload: { isLoading: true, isLoggingOut: true },
      });
      try {
        if (revokeStytchSession) {
          await stytch.session.revoke({ forceClear: true });
        }
        setPostHogAndDatadogUser();
        resetState();
        cancelAxiosRequests();
        clearSWRCache();
        // A token exchange is not a sign-out, so the sign-up draft has to survive it. A real
        // sign-out still drops the draft, or the next person to use this browser inherits the
        // company details the previous one typed.
        const onboardingDraft = preserveOnboardingDraft ? snapshotOnboardingDraft() : {};
        // Save values that must persist across logout before clearing localStorage
        const skip2FAValue = localStorage.getItem(THERA_Skip2FA);
        const lastUsedAuthMethod = localStorage.getItem(LS_key_Last_Used_Auth_Method);
        const superAdminValue = localStorage.getItem(THERA_SuperAdmin);
        const supportAdminValue = localStorage.getItem(THERA_SupportAdmin);
        const skipEmailVerifyValue = localStorage.getItem(THERA_SkipEmailVerify);
        const referralBannerDismissedValue = isReferralBannerDismissed();
        // Theme is a device preference, not session state, so keep it across logout.
        const themeValue = localStorage.getItem(THEME_STORAGE_KEY);
        clearLocalStorage();
        localStorage.clear();
        restoreOnboardingDraft(onboardingDraft);
        if (themeValue) {
          localStorage.setItem(THEME_STORAGE_KEY, themeValue);
        }
        if (referralBannerDismissedValue) {
          markReferralBannerDismissed();
        }
        // Restore THERA_Skip2FA value if it was previously set by the user
        // Note: We only restore based on the user's prior localStorage value, not the env var,
        // to avoid persisting 2FA bypass from development builds into production
        if (skip2FAValue === 'true') {
          localStorage.setItem(THERA_Skip2FA, 'true');
        }
        // Restore lastUsedAuthMethod value if it was set
        if (lastUsedAuthMethod) {
          localStorage.setItem(LS_key_Last_Used_Auth_Method, lastUsedAuthMethod);
        }
        // Restore admin mode flags so impersonation persists across login session switches.
        // Explicit "Logout from Admin Mode" (turnOffAdminMode) uses removeItem directly, not logout().
        if (superAdminValue === 'true') {
          localStorage.setItem(THERA_SuperAdmin, 'true');
        }
        if (supportAdminValue === 'true') {
          localStorage.setItem(THERA_SupportAdmin, 'true');
        }
        if (skipEmailVerifyValue) {
          localStorage.setItem(THERA_SkipEmailVerify, skipEmailVerifyValue);
        }
        if (redirectToSignIn && router.pathname !== AuthRoutes.SignIn && !isAlwaysPublicRoute(router.pathname)) {
          await router.push(AuthRoutes.SignIn);
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({
          type: actionTypes.SET_LOADING_AND_LOGGING_OUT,
          payload: { isLoading: false, isLoggingOut: false },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stytch],
  );

  const refreshSession = useCallback(async () => {
    const _session = stytch.session.getSync();
    if (_session) {
      console.info('refreshing session!');
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      try {
        await stytch.session.authenticate({ session_duration_minutes: SESSION_DURATION_MINUTES });
      } catch (err) {
        console.error(err);
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    }
  }, [stytch]);

  // A Google sign-in the allow list turned away keeps holding its address, so an admin can't move a worker onto it.
  // The backend decides whether deleting it is safe.
  const discardDeniedSignIn = useCallback(
    async (stytchUserId: string) => {
      try {
        const { discarded } = await theraFernApi.thera.session.sessionApi.discardDeniedSignIn(stytchUserId);
        if (!discarded) return;
        // The session died with the user, so revoking it throws after clearing it locally. Swallowed here
        // so logout still resets state, storage and analytics.
        await stytch.session.revoke({ forceClear: true }).catch(() => undefined);
        await logout(false, false);
      } catch (error) {
        logError(error, { source: 'discard_denied_sign_in' });
      }
    },
    [theraFernApi, stytch, logout],
  );

  const fetchUserProfile = useCallback(
    async ({
      shouldRefreshSession = false,
      newCompanyId = '',
      shouldReturnAfterRefresh = false,
    }: {
      shouldRefreshSession?: boolean;
      newCompanyId?: string;
      shouldReturnAfterRefresh?: boolean;
    } = {}) => {
      // Capture readiness at the start of the call.
      // This prevents background refresh failures from showing the generic toast
      // just because `isUserProfileReady` may be temporarily toggled during routing decisions.
      const wasUserProfileReadyAtStart = isUserProfileReadyRef.current;

      // if stytch is not available, return
      if (!stytchUser || !session || state.isLoggingOut || hasSessionExpired) {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
        return null;
      }

      // When setting up TOTP (2FA), Stytch's API calls trigger updates to stytchUser
      // This causes multiple re-renders and fetchUserProfile calls due to the stytchUser dependency
      // During TOTP setup flow, we want to prevent these re-renders and profile refetches
      // as they can interrupt the setup process and cause unwanted state updates
      // So we check isTOTPSetupInProgress flag and return early if true
      if (state.isTOTPSetupInProgress) {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
        return null;
      }

      // if there is a need to refresh the session, refresh and return
      if (shouldRefreshSession) {
        await refreshSession();
        if (shouldReturnAfterRefresh) {
          return null;
        }
      }

      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      // An empty emails array is still truthy, so index before reading rather than after.
      const email = stytchUser?.emails?.[0]?.email ?? null;
      const userId = stytchUser?.user_id;
      const name = stytchUser?.name?.first_name ? `${stytchUser?.name?.first_name} ${stytchUser?.name?.last_name}` : '';
      setPostHogAndDatadogUser({ email, userId, name });

      // Derive admin mode from session claims first (prevents localStorage forgery).
      // With no claim, the checkIf* helpers honor localStorage in non-prod Stytch environments only.
      const sessionAdminType = session?.custom_claims?.admin_type as string | undefined;
      const isSuperAdminMode =
        typeof sessionAdminType === 'string' ? sessionAdminType === 'super' : checkIfSuperAdminMode();
      const isSupportAdminMode =
        typeof sessionAdminType === 'string' ? sessionAdminType === 'support' : checkIfSupportAdminMode();

      // Admin sessions skip email verification. Verification proves the end user controls their
      // own mailbox, so it withholds nothing an admin session does not already hold, and gating
      // support on it left agents unable to open the accounts they get tickets about: an invite
      // sent to a mistyped address strands that user on the verify screen indefinitely.
      // Both flags are session-derived so a support admin with THERA_SuperAdmin set in
      // localStorage cannot forge the wider super-admin bypass.
      const isEmailVerified = isSuperAdminMode || isSupportAdminMode ? true : await checkIfEmailVerified(stytchUser);
      const isNewInvitedUserWithoutPasswordAndNoOAuth = checkIfNewInvitedUserWithoutPasswordAndNoOAuth(stytchUser);

      // check if user can skip 2FA
      const skip2FAForEmail = SKIP_2FA_EMAILS?.includes(email);
      const canSkip2FA =
        isSuperAdminMode ||
        isSupportAdminMode ||
        (!isStytchProdEnv && localStorage.getItem(THERA_Skip2FA) === 'true') ||
        skip2FAForEmail ||
        skip2FA;

      // Read the live session, not the closure snapshot: this callback can be a stale closure
      // held by the OTP modal, whose captured session still predates verification.
      const liveSessionFactorCount = (stytch.session.getSync() ?? session)?.authentication_factors?.length ?? 0;

      // Determine 2FA verification status early - this is needed for MultiFactorAuthentication component
      const is2FAVerified = canSkip2FA || liveSessionFactorCount >= 2;

      // Early set of 2FA status so MultiFactorAuthentication can work properly
      // Only set isUserProfileReady to false if we don't already have user profile data
      // This prevents losing account switcher access on subsequent failed fetches
      dispatch({
        type: actionTypes.SET_USER_PROFILE_DATA,
        payload: {
          user: { ...currentUser, email, isEmailVerified },
          canSkip2FA,
          is2FAVerified,
          isUserProfileReady: state.isUserProfileReady ?? false,
        },
      });

      try {
        // get user profile and affiliations from Thera system using stytch userId
        const results = await Promise.allSettled([getUserProfile(userId), getUserAffiliations(userId)]);

        // Handle results and their potential errors separately
        if (results[0].status === 'rejected') {
          const error = results[0].reason;
          if (error?.httpStatus === 404) {
            // 404 means user profile does not exist in Thera system,
            // workers are invited by admins, so they would exist in Stytch as well as in Thera system,
            // but admins when signing up would not exist in Thera system, so we need to check if they can create an account
            // Handle 404 for user profile specifically
            try {
              routeToRedirect = await getStytch404RedirectRoute({
                email,
                checkCanCreateAccount: async () =>
                  (await theraFernApi.thera.company.companyApi.checkCanCreateAccount({ email })).authorized,
              });
              const landed = await router.replace(routeToRedirect);
              // Only once the public booking page is up: signing out on any other route sends them to sign-in.
              if (landed && routeToRedirect === AuthRoutes.SignUpNeedsBooking) await discardDeniedSignIn(userId);
              return null;
            } catch (authError) {
              theraToast.error({
                message: 'Failed to check account creation authorization:',
                error: authError,
                options: {
                  id: 'failed-to-check-account-creation-authorization',
                },
              });
              return null;
            }
          }
          throw error; // Re-throw non-404 errors for user profile
        }

        const userProfileRes = results[0].value;

        // Handle affiliations result
        if (results[1].status === 'rejected') {
          logError(new Error('Failed to fetch user affiliations'), {
            source: 'getUserAffiliations',
            data: results[1].reason,
          });
          return null;
        }

        const userAffiliationsRes = results[1].value;

        // update currentUser with userProfileRes and isEmailVerified
        // eslint-disable-next-line react-hooks/exhaustive-deps
        currentUser = {
          ...currentUser,
          ...userProfileRes,
          isEmailVerified,
        };

        const { affiliationsByCompany } = userAffiliationsRes || { affiliationsByCompany: null };

        // Malformed response (null or missing field): backend error, not a real revocation.
        // Show the generic "something went wrong" fallback instead of misleading revocation copy.
        if (!affiliationsByCompany) {
          logError(new Error('Affiliations response malformed'), {
            source: 'getUserAffiliations',
            data: { userAffiliationsRes },
          });
          dispatch({ type: actionTypes.SET_HAS_AFFILIATIONS_ERROR, payload: true });
          dispatch({ type: actionTypes.SET_USER, payload: currentUser });
          return null;
        }

        // Well-formed empty map: every active affiliation removed → access revoked.
        if (!Object.keys(affiliationsByCompany).length) {
          dispatch({ type: actionTypes.SET_HAS_NO_AFFILIATIONS, payload: true });
          dispatch({ type: actionTypes.SET_USER, payload: currentUser });
          theraToast.warning('Your organization has revoked your access. Please contact your organization admin.', {
            id: 'no-data-found-for-user',
          });
          return null;
        }

        // Reset the flags if we have affiliations
        dispatch({ type: actionTypes.SET_HAS_NO_AFFILIATIONS, payload: false });
        dispatch({ type: actionTypes.SET_HAS_AFFILIATIONS_ERROR, payload: false });

        const ls_affiliations_saved_values = get_ls_saved_values();
        const userSavedCompanyId = ls_affiliations_saved_values?.companyId;
        const userRoleType = ls_affiliations_saved_values?.roleType;
        const userSubRoleType = ls_affiliations_saved_values?.subRoleType;

        // transform the affiliations to populate the contract object
        const transformedAffiliations: Record<string, ICompanyAffiliation> =
          getTransformedAffiliations(affiliationsByCompany);

        const {
          userCurrentSubRoleType,
          userCurrentRoleType,
          updatedUser,
          userCurrentCompany,
          userCurrentContract,
          canShowAccountSwitcher,
          redirectRoute,
        } = await getUserContextData({
          user: currentUser,
          newCompanyId,
          affiliationsByCompany: transformedAffiliations,
          userRoleType,
          userSubRoleType,
          userSavedCompanyId,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
        userFullName = `${userProfileRes.firstName} ${userProfileRes.lastName}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
        userCreatedAtTimestamp = DateTime.fromISO(stytchUser?.created_at).toSeconds();

        if (isEmailVerified) {
          // if email is verified, remove hasSentFirstEmail from local storage
          clearLSEmailProps(email);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
        routeToRedirect = redirectRoute;

        // For US Based Company, check if banking details are submitted
        // We specifically check for callerUserBeneficiaryInformation to ensure beneficiary onboarding (step 5) is complete
        // since incorporation data (step 4) is now saved separately via updateCompanyIncorporationInfo
        const isUSBasedCompany = userCurrentCompany?.address?.country === 'US';
        const isBankingDetailsSubmitted =
          isUSBasedCompany && Boolean(userCurrentCompany?.companyBankingDetails?.callerUserBeneficiaryInformation);
        const isManager = userCurrentRoleType === 'manager';
        const isIncorporationDetailsSubmitted = isUSBasedCompany && Boolean(userCurrentCompany?.incorporationDetails);

        // After onboarding, the manager must add at least one payment method before reaching the
        // dashboard. The check is here (next to the other onboarding gates) rather than in HomePage
        // so the redirect fires regardless of which path the user lands on. Mirrors the historical
        // select-apps gate that lived in this same ladder.
        // Default to true so a genuine network failure doesn't penalize the user; only 404 (BE's
        // signal for "no payment methods exist", per usePaymentMethodsSWR) flips it to false.
        let hasAnyPaymentMethod = true;
        if (isManager && userCurrentCompany?.companyID && !checkIfSuperAdminMode() && !isSupportAdminMode) {
          try {
            const pmRes = await theraFernApi.thera.bank.bankApi.getPaymentMethods(userCurrentCompany.companyID);
            hasAnyPaymentMethod = (pmRes?.methods?.length ?? 0) > 0;
          } catch (pmErr: any) {
            const pmStatus = pmErr?.statusCode ?? pmErr?.status ?? pmErr?.httpStatus ?? pmErr?.response?.status;
            if (pmStatus === 404) {
              hasAnyPaymentMethod = false;
            }
            // Other (network/5xx) errors: keep safe default so we don't redirect into the gate.
          }
        }

        if (
          isUSBasedCompany &&
          !isBankingDetailsSubmitted &&
          !userCurrentCompany?.settings?.isTpOnboardingSkipped &&
          userCurrentCompany?.tp?.payrollFboOnboardingStage === 'BANKING_DETAILS' &&
          isManager
        ) {
          routeToRedirect = `${AuthRoutes.SignUpOnboarding}`;
          console.info('redirect to sign up onboarding!');
        } else if (
          !router.pathname.includes(AdminRoutes.TPOnboarding) &&
          !router.pathname.includes('/banking') && // Do not redirect tp-onboarding to dashboard
          isUSBasedCompany &&
          isBankingDetailsSubmitted &&
          userCurrentCompany?.settings?.isTpOnboardingSkipped &&
          // is Manager
          isManager &&
          !router.pathname.includes(AdminRoutes.BulkContractorUpload) // isTpOnboardingSkipped is only true when user has finished the onboarding, that means user has verified the email and set the password
        ) {
          console.info('banking onboarding done!');
          routeToRedirect = `${AdminRoutes.Dashboard}`;
        } else if (
          isManager &&
          !hasAnyPaymentMethod &&
          !isIncorporationDetailsSubmitted &&
          !hasSkippedPaymentMethodOnboarding()
        ) {
          // Payment-method onboarding is part of the sign-up flow — it preempts email-verify and
          // set-password (and the MFA HOC suppresses its prompt on this route) so that a manager
          // can finish their onboarding step before being asked to verify or enroll in 2FA.
          // The Skip-flag check stops the redirect loop after the user dismisses the page.
          console.info('redirect to add payment method!');
          routeToRedirect = `${AuthRoutes.SignUpAddPaymentMethod}`;
        } else if (!isEmailVerified) {
          // for both worker and manager, if email is not verified, redirect to email verification page
          // Always redirect to email verification first if email is not verified
          const redirectToEmailVerification = routeToRedirectToEmailVerification(email, router.pathname);
          if (redirectToEmailVerification) {
            routeToRedirect = redirectToEmailVerification;
          }
        } else if (isNewInvitedUserWithoutPasswordAndNoOAuth) {
          // if user is invited and has not set password and has no oauth,
          // then redirect to set password page only if user is not in super admin mode
          const redirectToSetPassword = routeToRedirectToSetPassword(router.pathname);
          if (redirectToSetPassword) {
            routeToRedirect = redirectToSetPassword;
          }
        }

        // Arm the switcher only once the MFA requirement is met; a pre-OTP fetch arming it
        // makes the modal show, hide behind the post-OTP refetch loader, then show again.
        const isMFARequirementSatisfied =
          canSkip2FA || liveSessionFactorCount >= (currentUser?.mfaUserDetails?.mfaType ? 2 : 1);

        dispatch({
          type: actionTypes.SET_USER_PROFILE_DATA,
          payload: {
            user: { ...currentUser, ...updatedUser },
            roleType: userCurrentRoleType,
            subRoleType: userCurrentSubRoleType,
            affiliationsByCompany: transformedAffiliations,
            currentCompany: userCurrentCompany,
            currentContract: userCurrentContract as unknown as IEmployment,
            currentContractId: userCurrentContract?.id,
            showAccountSwitcher: canShowAccountSwitcher && isMFARequirementSatisfied,
            canSkip2FA,
            is2FAVerified,
            isUserProfileReady: false, // Still not ready yet, routing decisions need to be made
          },
        });

        setPostHogAndDatadogUser({
          email,
          userId,
          name: userFullName,
          userRoleType: userCurrentRoleType,
          userSubRoleType: userCurrentSubRoleType,
        });

        updateIntercomSettings({
          name: userFullName,
          email,
          created_at: userCreatedAtTimestamp,
          role: userCurrentRoleType,
          user_id: userId,
          companies: buildIntercomCompanies(transformedAffiliations),
        });

        if (!router?.isReady) {
          return null;
        }

        if (isAuthenticating) {
          return null;
        }

        if (routeToRedirect) {
          await router.replace(routeToRedirect);
        }

        // Mark user profile as ready after all routing decisions have been made
        // This prevents the account switcher from showing prematurely during 2FA completion
        dispatch({
          type: actionTypes.SET_USER_PROFILE_READY,
          payload: true,
        });

        // Return the updated user object
        return { ...currentUser, ...updatedUser };
      } catch (error) {
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED' || error?.message === 'Request_Cancelled') {
          console.warn('Fetch aborted');
        } else {
          // If the user profile is already ready, we don't want to show a generic error toast
          // as it might be a background refresh failure which is not critical for the user at that moment.
          // We still log it to Datadog for monitoring.
          const shouldShowToast = !wasUserProfileReadyAtStart;

          // The most common cause of a failed profile bootstrap is the user's own network/ISP
          // dropping the request before it reaches our API: the call returns with no HTTP status,
          // surfacing as an axios "Network Error". Attach connectivity diagnostics so these are
          // distinguishable from genuine app errors in Datadog instead of an opaque generic toast.
          const failureContext = {
            failure_category: shouldShowToast ? 'onboarding_profile_fetch' : 'background_profile_refresh',
            browser_online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
            is_connectivity_failure:
              (typeof navigator !== 'undefined' && navigator.onLine === false) ||
              ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) ||
              /network error|failed to fetch|timeout/i.test(error?.message ?? ''),
            error_code: error?.code,
            failed_request_url: error?.config?.url,
            failed_request_method: error?.config?.method,
          };

          if (shouldShowToast) {
            theraToast.error({
              message: 'An unexpected error occurred, please try again or contact Thera support if it persists.',
              error: error,
              options: {
                id: 'unexpected-error-during-user-profile-fetch',
                sendToSlack: true,
                data: failureContext,
              },
            });
          } else {
            // Background profile refresh failure: log for monitoring, but don't double-toast the user.
            const errorToLog = error instanceof Error ? error : new Error('fetchUserProfile failed');
            // Use captureError directly to avoid the theraToast + logError redundancy rule.
            // Mark as non-user-facing to keep it out of Slack paging.
            captureError(errorToLog, {
              source: 'fetchUserProfile',
              isToastError: false,
              data: {
                ...failureContext,
                isUserProfileReady: isUserProfileReadyRef.current,
                wasUserProfileReadyAtStart,
                state: { ...state },
                ...(error instanceof Error ? {} : { originalError: error }),
              },
            });
          }
        }
        return null;
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticating, stytchUser, hasSessionExpired, state.isTOTPSetupInProgress], // rerender is only needed when the user has authenticated, the session has expired, or the password has changed
  );

  // A public page needs nothing about whoever is signed in. Loading their account is what redirects a
  // stuck account to onboarding or verification, raises account toasts, and boots Intercom.
  const isPublicRoute = isAlwaysPublicRoute(router.pathname);

  useEffect(() => {
    // Fetch user profile when critical auth state changes, but not on every stytchUser reference change
    // Use stytchUser.user_id instead of stytchUser to avoid loops from Stytch's internal object updates
    if (!isAuthenticating && !isPublicRoute) {
      fetchUserProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticating, stytchUser?.user_id, hasSessionExpired, state.isTOTPSetupInProgress, isPublicRoute]); // Critical auth state only

  const memoizedUser = useMemo(() => ({ ...state.user }), [state.user]);
  const memoizedAffiliations = useMemo(() => ({ ...state.affiliationsByCompany }), [state.affiliationsByCompany]);

  // Memoize the exposed value to prevent unnecessary re-renders
  const exposed = useMemo(
    () => ({
      user: memoizedUser,
      setUser: (user) => dispatch({ type: actionTypes.SET_USER, payload: user }),
      logout,
      canSkip2FA: state.canSkip2FA,
      is2FAVerified: state.is2FAVerified,
      setIs2FAVerified: (isVerified) => dispatch({ type: actionTypes.SET_IS_2FA_VERIFIED, payload: isVerified }),
      mutate: fetchUserProfile,
      isLoading: state.isLoading,
      isLoggingOut: state.isLoggingOut,
      affiliationsByCompany: memoizedAffiliations,
      currentTeamId: state.currentTeamId,
      setCurrentTeamId: (teamId) => dispatch({ type: actionTypes.SET_CURRENT_TEAM_ID, payload: teamId }),
      currentContractId: state.currentContractId,
      setCurrentContractId: (contractId) =>
        dispatch({ type: actionTypes.SET_CURRENT_CONTRACT_ID, payload: contractId }),
      currentContract: state.currentContract,
      setCurrentContract: (contract) => dispatch({ type: actionTypes.SET_CURRENT_CONTRACT, payload: contract }),
      currentCompany: state.currentCompany,
      setCurrentCompany: (company) => dispatch({ type: actionTypes.SET_CURRENT_COMPANY, payload: company }),
      roleType: state.roleType,
      setRoleType: (roleType) => dispatch({ type: actionTypes.SET_ROLE_TYPE, payload: roleType }),
      subRoleType: state.subRoleType,
      setSubRoleType: (subRoleType) => dispatch({ type: actionTypes.SET_SUB_ROLE_TYPE, payload: subRoleType }),
      showAccountSwitcher: state.showAccountSwitcher,
      setShowAccountSwitcher: (show) => dispatch({ type: actionTypes.SET_SHOW_ACCOUNT_SWITCHER, payload: show }),
      showAllTeamsContracts: !state.currentTeamId,
      isAccountSwitchingInProgress: state.isAccountSwitchingInProgress,
      setIsAccountSwitchingInProgress: (inProgress) =>
        dispatch({ type: actionTypes.SET_ACCOUNT_SWITCHING, payload: inProgress }),
      isTOTPSetupInProgress: state.isTOTPSetupInProgress,
      setTOTPSetupStatus: (status) => dispatch({ type: actionTypes.SET_TOTP_SETUP_STATUS, payload: status }),
      isUserProfileReady: state.isUserProfileReady,
      setIsUserProfileReady: (isReady) => dispatch({ type: actionTypes.SET_USER_PROFILE_READY, payload: isReady }),
      hasNoAffiliations: state.hasNoAffiliations,
      hasAffiliationsError: state.hasAffiliationsError,
    }),
    [
      memoizedUser,
      logout,
      fetchUserProfile,
      state.isLoading,
      state.isLoggingOut,
      memoizedAffiliations,
      state.currentTeamId,
      state.currentContractId,
      state.currentContract,
      state.currentCompany,
      state.roleType,
      state.subRoleType,
      state.showAccountSwitcher,
      state.isAccountSwitchingInProgress,
      state.isTOTPSetupInProgress,
      state.canSkip2FA,
      state.is2FAVerified,
      state.isUserProfileReady,
      state.hasNoAffiliations,
      state.hasAffiliationsError,
    ],
  );

  return <UserContext.Provider value={exposed}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);

export default UserProvider;
